import { z } from 'zod'

const timestamp = z.string().datetime({ offset: true })

export const navigationSnapshotSchema = z
  .object({
    availableGameweeks: z.array(
      z
        .object({
          deadline: timestamp,
          end_date: timestamp.nullable(),
          gameweek_number: z.number().int().positive(),
          id: z.string().uuid(),
          start_date: timestamp.nullable(),
          status: z.enum(['past', 'current', 'future'])
        })
        .strict()
    ),
    gameweekInfo: z
      .object({
        currentDeadline: timestamp,
        currentGameweek: z.number().int().min(0),
        deadline: timestamp,
        nextGameweekStart: timestamp.optional(),
        season: z.string().min(1).max(40),
        timeUntilDeadline: z.string().min(1).max(80)
      })
      .strict(),
    liveMatches: z.array(
      z
        .object({
          awayScore: z.number().finite(),
          awayTeam: z.string().max(160),
          homeScore: z.number().finite(),
          homeTeam: z.string().max(160),
          id: z.string().min(1).max(160),
          minute: z.number().int().min(0).max(240),
          status: z.string().max(80)
        })
        .strict()
    ),
    notifications: z
      .object({ hasHighPriority: z.boolean(), unreadCount: z.number().int().min(0) })
      .strict(),
    tokenBalances: z
      .object({
        neuroTokens: z.number().finite().min(0),
        paperTokens: z.number().finite().min(0),
        primaryTokenBalance: z.number().finite().min(0),
        primaryTokenLabel: z.string().min(1).max(40),
        primaryTokenType: z.enum(['neuro', 'paper']),
        prizeTokens: z.number().finite().min(0)
      })
      .strict(),
    userProfile: z
      .object({
        experiencePoints: z.number().int().min(0),
        isPrizeEligible: z.boolean(),
        nextLevelXP: z.number().int().min(0),
        subscriptionMonths: z.number().int().min(0),
        userLevel: z.number().int().positive()
      })
      .strict(),
    userRank: z
      .object({
        currentRank: z.number().int().min(0),
        rankChange: z.number().int(),
        totalPoints: z.number().finite()
      })
      .strict()
  })
  .strict()

export type NavigationSnapshot = z.infer<typeof navigationSnapshotSchema>
export type TokenBalances = NavigationSnapshot['tokenBalances']
export type GameweekInfo = NavigationSnapshot['gameweekInfo']
export type NotificationData = NavigationSnapshot['notifications']
export type UserRankData = NavigationSnapshot['userRank']
export type NavigationLiveMatch = NavigationSnapshot['liveMatches'][number]
export type UserProfileData = NavigationSnapshot['userProfile']
export type NavigationGameweek = NavigationSnapshot['availableGameweeks'][number]
